import { useLocalization } from "@fluent/react";
import { Button, Row } from "@narmi/design_system";
import { useCallback, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { Card } from "@lending/components/Card/Card";
import { PageContent } from "@lending/components/PageContent";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { useLendingCaptcha } from "@lending/hooks/useLendingCaptcha";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import { InfoAlert } from "@lending/pages/Common/components/InfoAlert";
import { PersonalInfoEditForm } from "./components/PersonalInfoEditForm";
import { PersonalInfoSummary } from "./components/PersonalInfoSummary";
import { AddressSaveError, useCreatePlaidApplication } from "./hooks/useCreatePlaidApplication";
import { usePersonalInfoReviewForm } from "./hooks/usePersonalInfoReviewForm";
import styles from "./PersonalInfoReview.module.css";
import { useStore } from "../../../../contexts/StoreContext";
import type { PersonalInfoReviewFormData } from "./components/PersonalInfoEditForm";
import type { ExistingApplicationInfo } from "./hooks/useCreatePlaidApplication";

function PlaidPiiReview() {
  const { l10n } = useLocalization();
  const { goBack, goNext } = useFlowNavigation();
  const { updateData } = useStore();
  const [isEditing, setIsEditing] = useState(false);
  const {
    captchaValue,
    handleRecaptchaError,
    recaptchaRef,
    validateCaptcha,
    setCaptchaValue,
    shouldShowCaptcha,
    siteKey,
  } = useLendingCaptcha();

  const {
    control,
    handleSubmit,
    getValues,
    setError,
    formValues,
    errors,
    dirtyFields,
    initialHasMissingFields,
    isLoading,
    identity,
    applicant,
    sensitiveApplicant,
    applicationUuid,
  } = usePersonalInfoReviewForm();

  const { submitApplication, isSubmitting } = useCreatePlaidApplication();
  const { showErrorToast } = useToastsContext();

  const saveFormDataToStore = useCallback(
    (data: PersonalInfoReviewFormData) => {
      updateData({ plaidPiiFormData: data });
    },
    [updateData]
  );

  const buildExistingInfo = useCallback((): ExistingApplicationInfo | undefined => {
    if (!applicationUuid || !applicant?.id) {
      return undefined;
    }

    return {
      applicationUuid,
      applicantDetailsId: applicant.id,
    };
  }, [applicationUuid, applicant?.id]);

  const onSubmit = handleSubmit(
    async (data) => {
      saveFormDataToStore(data);
      try {
        const existingInfo = buildExistingInfo();
        // A DOB edit restored from the store after Back isn't dirty, so also compare it with the saved one.
        const isSsnMasked = data.tax_id.startsWith("*");
        const hasSensitiveFieldsChanged =
          !!dirtyFields.tax_id ||
          !!dirtyFields.date_of_birth ||
          (isSsnMasked && data.date_of_birth !== sensitiveApplicant?.date_of_birth);

        // A sensitive change starts a new application, which needs the real SSN, not the masked one.
        if (hasSensitiveFieldsChanged && isSsnMasked) {
          const message = l10n.getString(
            "plaid-pii-review-reenter-ssn-error",
            null,
            "Re-enter your full SSN to update your date of birth."
          );
          setError("tax_id", { message });
          showErrorToast(message);
          return;
        }

        if (!validateCaptcha()) {
          return;
        }

        const { application } = await submitApplication(
          data,
          existingInfo,
          hasSensitiveFieldsChanged,
          captchaValue
        );
        goNext({ applicationUuid: application.id });
      } catch (error) {
        if (handleRecaptchaError(error)) {
          return;
        }
        if (error instanceof AddressSaveError) {
          showErrorToast(error.message);
        } else {
          showErrorToast(
            l10n.getString(
              "error-application-create",
              null,
              "There was an error creating your application. Please try again."
            )
          );
        }
      }
    },
    () => {
      if (!isEditing) {
        showErrorToast(
          l10n.getString("plaid-pii-review-missing-fields-error", null, "Missing required fields.")
        );
      }
    }
  );

  const handleBack = useCallback(() => {
    saveFormDataToStore(getValues());
    goBack();
  }, [saveFormDataToStore, getValues, goBack]);

  const heading = initialHasMissingFields
    ? l10n.getString("plaid-pii-review-heading-missing", null, "Almost there!")
    : l10n.getString("plaid-pii-review-heading", null, "Looking good!");

  const subheading = initialHasMissingFields
    ? l10n.getString(
        "plaid-pii-review-subheading-missing",
        null,
        "We found most of your information. Review and make any updates below."
      )
    : l10n.getString(
        "plaid-pii-review-subheading",
        null,
        "Review your details and make any changes below."
      );

  return (
    <PageLayout isLoading={isLoading}>
      <PageContent>
        <PageHeader heading={heading} subheading={subheading} />
        {!isLoading && (
          <>
            <div className={styles.infoContainer}>
              <Card className="rounded--all--l">
                <div className={styles.cardHeader}>
                  <span className={styles.cardTitle}>
                    {l10n.getString("plaid-pii-review-card-title", null, "Your information")}
                  </span>
                  {!initialHasMissingFields && (
                    <button
                      type="button"
                      className={styles.editLink}
                      onClick={() => setIsEditing((prev) => !prev)}
                    >
                      {l10n.getString("plaid-pii-edit-personal-details", null, "Edit")}
                      <span
                        className={`${styles.editChevron} ${isEditing ? "narmi-icon-chevron-up" : "narmi-icon-chevron-down"}`}
                      ></span>
                    </button>
                  )}
                </div>
                <div className={styles.contentContainer}>
                  {!initialHasMissingFields && (
                    <PersonalInfoSummary
                      formValues={formValues}
                      errors={errors}
                      isEditing={isEditing}
                    />
                  )}
                  <div
                    className={`${styles.formLayer} ${initialHasMissingFields || isEditing ? styles.visible : ""}`}
                  >
                    <div className={styles.formLayerInner}>
                      <PersonalInfoEditForm control={control} isInitiallyMasked={!!identity?.ssn} />
                    </div>
                  </div>
                </div>
              </Card>
            </div>
            <div className="margin--bottom--xxl">
              <InfoAlert
                message={l10n.getString(
                  "confirm-info-alert",
                  null,
                  "Checking your rate will not affect your credit score."
                )}
              />
            </div>
            {shouldShowCaptcha && (
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={siteKey}
                onChange={setCaptchaValue}
                onExpired={() => setCaptchaValue(null)}
              />
            )}
          </>
        )}
        <Row alignItems="center" justifyContent="space-between">
          <BackButton onClick={handleBack} />
          <div className={styles.nextButton}>
            <Button
              label={l10n.getString("button-label-next", null, "Next")}
              disabled={isLoading}
              isLoading={isSubmitting}
              onClick={onSubmit}
              endIcon="chevron-right"
            />
          </div>
        </Row>
      </PageContent>
    </PageLayout>
  );
}

export { PlaidPiiReview };
